import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { db } from '../firebase/config';
import {
  doc,
  getDoc,
} from 'firebase/firestore';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount || 0));
};

// Green at/above 20% margin, red below; shade deepens the further from the threshold
const getMarginColor = (percent) => {
  const p = Number(percent) || 0;
  if (p >= 20) {
    const t = Math.min((p - 20) / 30, 1);
    return `hsl(120, 65%, ${42 - t * 18}%)`;
  }
  const t = Math.min((20 - p) / 20, 1);
  return `hsl(0, 70%, ${55 - t * 20}%)`;
};

export default function PublicProposal() {
  const { proposalId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [proposal, setProposal] = useState(null);
  const [client, setClient] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const ref = doc(db, 'proposals', proposalId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setError('Proposal not found.');
          setLoading(false);
          return;
        }
        const data = snap.data();
        if (!data.shareEnabled || !data.shareToken || data.shareToken !== token) {
          setError('This share link is invalid or disabled.');
          setLoading(false);
          return;
        }
        setProposal({ id: snap.id, ...data });

        if (data.client_id) {
          try {
            const clientRef = doc(db, 'clients', data.client_id);
            const clientSnap = await getDoc(clientRef);
            if (clientSnap.exists()) setClient({ id: clientSnap.id, ...clientSnap.data() });
          } catch {}
        }
      } catch (e) {
        setError('Failed to load proposal.');
      } finally {
        setLoading(false);
      }
    };
    if (proposalId && token) load();
  }, [proposalId, token]);

  const services = useMemo(() => {
    const costs = Array.isArray(proposal?.costs) ? proposal.costs : [];
    return costs.filter(c => !c.isNew);
  }, [proposal]);

  const partnerRoles = useMemo(() => (
    Array.isArray(proposal?.partnerRoles) ? proposal.partnerRoles : []
  ), [proposal]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" fontWeight="bold">{proposal.title}</Typography>
          <Chip size="small" label={proposal.status || 'Draft'} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {client?.clientName ? `Client: ${client.clientName}` : 'Client'}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Summary</Typography>
            <Box sx={{ color: 'text.primary' }} dangerouslySetInnerHTML={{ __html: proposal.notes || '' }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Key Opportunities</Typography>
            <Box sx={{ color: 'text.primary' }} dangerouslySetInnerHTML={{ __html: proposal.timeline || '' }} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Services</Typography>
        {services.length === 0 ? (
          <Alert severity="info">No services found for this proposal.</Alert>
        ) : (
          <Grid container spacing={2}>
            {services.map((s, idx) => (
              <Grid item xs={12} key={idx}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{s.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(s.frequency === 'monthly') ? 'Monthly' : 'One-time'} • Qty {s.quantity || 1} •{' '}
                          <Box component="span" sx={{ color: getMarginColor(s.margin_percent), fontWeight: 'bold' }}>
                            Margin {Number(s.margin_percent) || 0}%
                          </Box>
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" color="primary" fontWeight="bold">
                        {formatCurrency((Number(s.internal_cost)||0) * (1 + (Number(s.margin_percent)||0)/100) * (Number(s.quantity)||1))}
                      </Typography>
                    </Box>
                    {s.description && (
                      <Box sx={{ mt: 1, color: 'text.primary' }} dangerouslySetInnerHTML={{ __html: s.description }} />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Partner Roles</Typography>
        {partnerRoles.length === 0 ? (
          <Alert severity="info">No partner roles defined for this proposal.</Alert>
        ) : (
          <Grid container spacing={2}>
            {partnerRoles.map((r, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Card>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">{r.positionName}</Typography>
                      <Typography variant="body2" color="text.secondary">{r.entityName || 'Unassigned'}</Typography>
                    </Box>
                    <Typography variant="subtitle1" color="primary" fontWeight="bold">
                      {Number(r.percent) || 0}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="h6" fontWeight="bold">
            Total: {formatCurrency(proposal.totalAmount || 0)}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}


